"use client";

import { useEffect, useMemo } from "react";

import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import { getCityNameByCode } from "@repo/ui/utils/flight";

import { useAppDispatch, useAppSelector } from "../hooks";
import { getRouteCountriesFromStations } from "../utils/passengerBookingInfo";
import { fetchStationsThunk } from "./stationsSlice";

const useStationsMap = () => {
  const dispatch = useAppDispatch();
  const stations = useAppSelector((state) => state.stations?.stations);
  const loading = useAppSelector((state) => state.stations?.isLoading);
  const error = useAppSelector((state) => state.stations?.error);

  useEffect(() => {
    if (!stations && !loading && !error) {
      dispatch(fetchStationsThunk(getTabSessionId() ?? ""));
    }
  }, [stations, loading, error, dispatch]);

  return stations;
};

/**
 * Resolve a station code to its city name, lazily loading the stations map.
 *
 * Kept out of `stationsSlice.ts` on purpose: that module is imported by
 * `store.ts`, so importing `../hooks` (which reaches back into `store.ts`)
 * from the slice would close a module cycle and break Turbopack HMR.
 */
export const useStationNameByCode = (code?: string | null) => {
  const stations = useStationsMap();

  if (!code) return "";
  if (!stations || Object.keys(stations).length === 0) return code;
  return getCityNameByCode(code, stations) ?? code;
};

export const useRouteCountriesFromStations = (
  departureCityCode?: string,
  destinationCityCode?: string,
) => {
  const stations = useStationsMap();
  return useMemo(
    () => getRouteCountriesFromStations(stations, departureCityCode, destinationCityCode),
    [stations, departureCityCode, destinationCityCode],
  );
};
